/**
 * Schema validation and coercion utilities for note telemetry events
 * Ensures all payloads conform to expected structure and types
 */

import type {
  EditEventMetrics,
  NoteTelemetryEventType,
  NoteViewedMetrics,
  SectionEventMetrics,
  SelectionMetrics,
} from './noteTelemetry';

const ALLOWED_MODALITIES = [
  'mouse',
  'touch',
  'keyboard',
  'copy_button',
  'copy_all',
  'copy_selected',
] as const;

type AllowedModality = (typeof ALLOWED_MODALITIES)[number];

const ALLOWED_METADATA_KEYS = [
  'module_id',
  'section_id',
  'note_id',
  'section_title',
  'encounter_id',
] as const;

/**
 * Clamp a number between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Coerce a value to a number, returning 0 if invalid
 */
function toNumber(value: unknown): number {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

/**
 * Validate and coerce SelectionMetrics payload
 */
export function validateAndCoerceSelectionMetrics(
  payload: Partial<SelectionMetrics>,
): SelectionMetrics {
  const now = performance.now();

  // Validate and coerce selectionId
  const selectionId =
    typeof payload.selectionId === 'string' && payload.selectionId.length > 0
      ? payload.selectionId
      : crypto.randomUUID();

  // Validate and coerce docId
  const docId = typeof payload.docId === 'string' && payload.docId.length > 0 ? payload.docId : '';

  // Validate and coerce docVersion
  const docVersion =
    typeof payload.docVersion === 'string' || typeof payload.docVersion === 'number'
      ? String(payload.docVersion)
      : '0';

  // Validate and coerce chars
  const selected = Math.max(0, toNumber(payload.chars?.selected));
  const total = Math.max(0, toNumber(payload.chars?.total));
  // Use the provided percent if available (already calculated with section context),
  // otherwise fall back to simple selected/total calculation
  const percent =
    payload.chars?.percent !== undefined
      ? clamp(toNumber(payload.chars.percent), 0, 1)
      : clamp(total > 0 ? selected / total : 0, 0, 1);

  // Validate and coerce timings
  const startedAt = toNumber(payload.timings?.startedAt) || now;
  const lastChangedAt = toNumber(payload.timings?.lastChangedAt) || now;
  const settledAt =
    payload.timings?.settledAt !== undefined ? toNumber(payload.timings.settledAt) : undefined;
  const msToSettle =
    settledAt !== undefined && startedAt > 0 ? Math.max(0, settledAt - startedAt) : undefined;
  const copiedAt =
    payload.timings?.copiedAt !== undefined ? toNumber(payload.timings.copiedAt) : undefined;
  const msToCopy =
    copiedAt !== undefined && startedAt > 0 ? Math.max(0, copiedAt - startedAt) : undefined;

  // Validate and coerce modality
  const modality: AllowedModality | undefined =
    payload.modality && ALLOWED_MODALITIES.includes(payload.modality)
      ? payload.modality
      : undefined;

  // Validate and coerce isBackward
  const isBackward = typeof payload.isBackward === 'boolean' ? payload.isBackward : undefined;

  // Validate and coerce ua
  const ua = typeof payload.ua === 'string' ? payload.ua : navigator.userAgent;

  // Validate and coerce metadata (strip unknown keys)
  const metadata: SelectionMetrics['metadata'] = payload.metadata
    ? Object.fromEntries(
        Object.entries(payload.metadata).filter(([key]) =>
          ALLOWED_METADATA_KEYS.includes(key as (typeof ALLOWED_METADATA_KEYS)[number]),
        ),
      )
    : undefined;

  return {
    selectionId,
    docId,
    docVersion,
    chars: {
      selected,
      total,
      percent,
    },
    timings: {
      startedAt,
      lastChangedAt,
      ...(settledAt !== undefined && { settledAt }),
      ...(msToSettle !== undefined && { msToSettle }),
      ...(copiedAt !== undefined && { copiedAt }),
      ...(msToCopy !== undefined && { msToCopy }),
    },
    ...(modality && { modality }),
    ...(isBackward !== undefined && { isBackward }),
    ua,
    ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
  };
}

/**
 * Validate and coerce NoteViewedMetrics payload
 */
export function validateAndCoerceNoteViewedMetrics(
  payload: Partial<NoteViewedMetrics>,
): NoteViewedMetrics {
  const docId = typeof payload.docId === 'string' && payload.docId.length > 0 ? payload.docId : '';
  const docVersion =
    typeof payload.docVersion === 'string' || typeof payload.docVersion === 'number'
      ? String(payload.docVersion)
      : '0';

  const metadata: NoteViewedMetrics['metadata'] = payload.metadata
    ? Object.fromEntries(
        Object.entries(payload.metadata).filter(([key]) =>
          ALLOWED_METADATA_KEYS.includes(key as (typeof ALLOWED_METADATA_KEYS)[number]),
        ),
      )
    : undefined;

  return {
    docId,
    docVersion,
    ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
  };
}

/**
 * Validate and coerce SectionEventMetrics payload
 */
export function validateAndCoerceSectionEventMetrics(
  payload: Partial<SectionEventMetrics>,
): SectionEventMetrics {
  const docId = typeof payload.docId === 'string' && payload.docId.length > 0 ? payload.docId : '';
  const docVersion =
    typeof payload.docVersion === 'string' || typeof payload.docVersion === 'number'
      ? String(payload.docVersion)
      : '0';

  const metadata: SectionEventMetrics['metadata'] = payload.metadata
    ? Object.fromEntries(
        Object.entries(payload.metadata).filter(([key]) =>
          ALLOWED_METADATA_KEYS.includes(key as (typeof ALLOWED_METADATA_KEYS)[number]),
        ),
      )
    : undefined;

  const hoverDuration =
    payload.hoverDuration !== undefined ? Math.max(0, toNumber(payload.hoverDuration)) : undefined;

  const interactionCounts = payload.interactionCounts
    ? {
        ...(payload.interactionCounts.clicks !== undefined && {
          clicks: Math.max(0, Math.floor(toNumber(payload.interactionCounts.clicks))),
        }),
        ...(payload.interactionCounts.selects !== undefined && {
          selects: Math.max(0, Math.floor(toNumber(payload.interactionCounts.selects))),
        }),
        ...(payload.interactionCounts.copies !== undefined && {
          copies: Math.max(0, Math.floor(toNumber(payload.interactionCounts.copies))),
        }),
      }
    : undefined;

  return {
    docId,
    docVersion,
    ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
    ...(hoverDuration !== undefined && { hoverDuration }),
    ...(interactionCounts && Object.keys(interactionCounts).length > 0 && { interactionCounts }),
  };
}

/**
 * Validate and coerce EditEventMetrics payload
 */
export function validateAndCoerceEditEventMetrics(
  payload: Partial<EditEventMetrics>,
): EditEventMetrics {
  const docId = typeof payload.docId === 'string' && payload.docId.length > 0 ? payload.docId : '';
  const docVersion =
    typeof payload.docVersion === 'string' || typeof payload.docVersion === 'number'
      ? String(payload.docVersion)
      : '0';

  const metadata: EditEventMetrics['metadata'] = payload.metadata
    ? Object.fromEntries(
        Object.entries(payload.metadata).filter(([key]) =>
          ALLOWED_METADATA_KEYS.includes(key as (typeof ALLOWED_METADATA_KEYS)[number]),
        ),
      )
    : undefined;

  const charsBefore = Math.max(0, toNumber(payload.edit?.charsBefore));
  const charsAfter = Math.max(0, toNumber(payload.edit?.charsAfter));
  const charsDelta = charsAfter - charsBefore;
  const percentDelta =
    charsBefore > 0 ? clamp(Math.abs(charsDelta) / charsBefore, 0, 1) : charsDelta !== 0 ? 1 : 0;

  const savedAt = toNumber(payload.timings?.savedAt) || performance.now();
  const startedAt =
    payload.timings?.startedAt !== undefined ? toNumber(payload.timings.startedAt) : undefined;
  const msInEdit =
    startedAt !== undefined && savedAt > 0 ? Math.max(0, savedAt - startedAt) : undefined;

  const source =
    payload.source === 'manual' || payload.source === 'ai_edit' || payload.source === 'regeneration'
      ? payload.source
      : 'manual';

  return {
    docId,
    docVersion,
    edit: {
      charsBefore,
      charsAfter,
      charsDelta,
      percentDelta,
    },
    timings: {
      ...(startedAt !== undefined && { startedAt }),
      savedAt,
      ...(msInEdit !== undefined && { msInEdit }),
    },
    source,
    ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
  };
}

/**
 * Validate and coerce a note telemetry event payload based on event type
 */
export function validateAndCoerceNoteEvent(
  type: NoteTelemetryEventType,
  payload: Partial<SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics>,
): SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics {
  if (type === 'text_selected' || type === 'text_copied' || type === 'selection_update') {
    return validateAndCoerceSelectionMetrics(payload as Partial<SelectionMetrics>);
  }

  if (type === 'note_viewed') {
    return validateAndCoerceNoteViewedMetrics(payload as Partial<NoteViewedMetrics>);
  }

  if (type === 'section_rendered' || type === 'section_interacted') {
    return validateAndCoerceSectionEventMetrics(payload as Partial<SectionEventMetrics>);
  }

  if (type === 'section_edited') {
    return validateAndCoerceEditEventMetrics(payload as Partial<EditEventMetrics>);
  }

  // Fallback: try to validate as SelectionMetrics
  return validateAndCoerceSelectionMetrics(payload as Partial<SelectionMetrics>);
}
